import { useState, type ReactNode } from 'react';
import { Space, Button, Modal, Tooltip, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import { EditOutlined, DeleteOutlined, ExclamationCircleFilled, MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useResponsive } from '@/shared/lib/hooks';

interface ExtraAction {
  icon: ReactNode;
  onClick: () => void;
  loading?: boolean;
  tooltip?: string;
  label?: string;
}

interface ActionButtonsProps {
  onEdit?: () => void;
  onDelete?: () => void;
  isDeleting?: boolean;
  extraActions?: ExtraAction[];
}

export const ActionButtons = ({ onEdit, onDelete, isDeleting, extraActions }: ActionButtonsProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDeleteClick = () => {
    setIsModalOpen(true);
  };

  const handleConfirm = () => {
    onDelete?.();
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  if (!isDesktop) {
    const items: MenuProps['items'] = [
      ...(extraActions?.map((action, index) => ({
        key: `extra-${index}`,
        icon: action.icon,
        label: action.tooltip ?? action.label,
        disabled: isDeleting || action.loading,
        onClick: action.onClick,
      })) ?? []),
      ...(onEdit
        ? [
            {
              key: 'edit',
              icon: <EditOutlined />,
              label: t('actions.editButton'),
              disabled: isDeleting,
              onClick: onEdit,
            },
          ]
        : []),
      ...(onDelete
        ? [
            {
              key: 'delete',
              icon: <DeleteOutlined />,
              label: t('actions.deleteButton'),
              danger: true,
              disabled: isDeleting,
              onClick: handleDeleteClick,
            },
          ]
        : []),
    ];

    return (
      <>
        <Dropdown menu={{ items }} trigger={['click']} placement='bottomRight'>
          <Button
            type='text'
            icon={<MoreOutlined />}
            className='!rounded-full !w-10 !h-10'
            loading={isDeleting}
          />
        </Dropdown>

        <Modal
          open={isModalOpen}
          onOk={handleConfirm}
          onCancel={handleCancel}
          okText={t('actions.confirmDelete')}
          cancelText={t('common.cancel')}
          okButtonProps={{ danger: true, loading: isDeleting }}
          centered
          width={400}
          closable={false}
        >
          <div className='flex items-start gap-4 py-2'>
            <div className='flex-shrink-0 w-12 h-12 rounded-full bg-[var(--color-error-100)] flex items-center justify-center text-2xl text-[var(--color-error-600)]'>
              <ExclamationCircleFilled />
            </div>
            <div className='flex-1'>
              <h3 className='m-0 mb-2 text-lg font-semibold text-[var(--color-text)]'>{t('actions.deleteTitle')}</h3>
              <p className='m-0 text-sm text-[var(--color-text-secondary)] leading-6'>{t('actions.deleteDescription')}</p>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  return (
    <>
      <Space>
        {extraActions?.map((action, index) => {
          const btn = (
            <Button
              key={index}
              type='text'
              icon={action.icon}
              loading={action.loading}
              disabled={isDeleting}
              onClick={action.onClick}
              className='!rounded-full !w-10 !h-10 bg-[var(--color-info-100)]! !text-[var(--color-gray-900)] hover:!bg-[var(--color-info-200)] hover:!text-[var(--color-gray-900)]'
            />
          );
          return action.tooltip ? (
            <Tooltip key={index} title={action.tooltip}>
              {btn}
            </Tooltip>
          ) : (
            btn
          );
        })}
        {onEdit && (
          <Button
            disabled={isDeleting}
            type='text'
            icon={<EditOutlined />}
            className='!rounded-full !w-10 !h-10 bg-[var(--color-warning-100)]! !text-[var(--color-gray-900)] hover:!bg-[var(--color-warning-200)] hover:!text-[var(--color-gray-900)]'
            onClick={onEdit}
          />
        )}
        {onDelete && (
          <Button
            type='text'
            icon={<DeleteOutlined />}
            className='!rounded-full !w-10 !h-10 bg-[var(--color-error-100)]! !text-[var(--color-gray-900)] hover:!bg-[var(--color-error-200)] hover:!text-[var(--color-gray-900)]'
            disabled={isDeleting}
            loading={isDeleting}
            onClick={handleDeleteClick}
          />
        )}
      </Space>

      <Modal
        open={isModalOpen}
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText={t('actions.confirmDelete')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: true, loading: isDeleting }}
        centered
        width={400}
        closable={false}
      >
        <div className='flex items-start gap-4 py-2'>
          <div className='flex-shrink-0 w-12 h-12 rounded-full bg-[var(--color-error-100)] flex items-center justify-center text-2xl text-[var(--color-error-600)]'>
            <ExclamationCircleFilled />
          </div>
          <div className='flex-1'>
            <h3 className='m-0 mb-2 text-lg font-semibold text-[var(--color-text)]'>{t('actions.deleteTitle')}</h3>
            <p className='m-0 text-sm text-[var(--color-text-secondary)] leading-6'>{t('actions.deleteDescription')}</p>
          </div>
        </div>
      </Modal>
    </>
  );
};
